import { msg } from '~/utils/validation';
import { z } from 'zod';

// A blank input registered with `valueAsNumber` arrives as NaN rather than undefined, which a plain
// optional number rejects as invalid. Coerce that NaN to undefined so a blank field reads as
// "not provided" and the optional check passes (e.g. disabled squat/deadlift in benchpress comps).
function optionalNumber(schema: z.ZodNumber) {
  return z.preprocess(
    value => (typeof value === 'number' && Number.isNaN(value) ? undefined : value),
    schema.optional()
  );
}

// Shared shape for both result forms. competition_type, age_category and team are optional here and
// made conditionally required by the variants below, so both share a single inferred type.
const baseSchema = z.object({
  group: z.string({ message: msg.required }).transform(s => Number(s)),
  competition_type: z.string().optional(),
  age_category: z
    .string()
    .optional()
    .transform(s => (s ? Number(s) : undefined)),
  // Either a DOM string from the select or a numeric default value (pre-populated, locked team).
  // `.optional()` is required: the team select is only rendered for team comps, so the key is absent
  // from individual-comp submissions and zod 4 rejects absent keys unless explicitly optional.
  team: z
    .unknown()
    .optional()
    .transform((v): number | undefined => {
      if (v === '' || v === null || v === undefined) return undefined;
      const n = Number(v);
      return Number.isNaN(n) ? undefined : n;
    }),
  body_weight: z.number({ message: msg.required }).multipleOf(0.01),
  // Squat and deadlift are optional because they're disabled (and left empty) for benchpress
  // competitions; the form forces them to 0 on submit in that case.
  squat: optionalNumber(z.number().multipleOf(0.05)),
  benchpress: z.number({ message: msg.required }).multipleOf(0.05),
  deadlift: optionalNumber(z.number().multipleOf(0.05)),
  lot: z
    .literal('')
    .transform(() => undefined)
    .or(z.coerce.number())
    .optional(),
  rank: z
    .literal('')
    .transform(() => undefined)
    .or(z.coerce.number())
    .optional(),
  is_coef_group: z.boolean().optional(),
  disqualified: z.boolean(),
  good_lifts: optionalNumber(z.number()),
  good_lifts_bp: optionalNumber(z.number()),
});

export type Schema = z.infer<typeof baseSchema>;

// Individual competitions: the registrar picks competition type and age category.
export const resultSchema = baseSchema.superRefine((data, ctx) => {
  if (!data.competition_type) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: msg.required, path: ['competition_type'] });
  }
  if (!data.age_category) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: msg.required, path: ['age_category'] });
  }
});

// Team competitions: the backend derives competition type and age category from the team's division,
// so only the team is required.
export const teamResultSchema = baseSchema.superRefine((data, ctx) => {
  if (data.team == null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: msg.required, path: ['team'] });
  }
});
